/**
 * BillsCard.tsx — the patient's billing-history card, shared by the Dashboard
 * overview and the "My orders" section (previously duplicated in both).
 *
 * Owner repo: @thegpservice/gpsclinics-portal. Slice: dashboard bills grouping
 * (2026-07-25).
 *
 * WHY: a patient with more than one order in flight saw a flat list of payments
 * with no indication which order (and so which plan price) each belonged to — a
 * £99 held payment for one order read as inconsistent with a £149/mo plan on
 * another. Bills now GROUP by their `orderCaseId` (sent by the orders projection)
 * and each group is labelled with that order's medication (resolved from the
 * dashboard's `orders`), so a payment is always read against its OWN order. When
 * the backend sends no `orderCaseId` (contract-conformant) or a group has no
 * resolvable name, it degrades to a clean flat list. This is the patient's OWN
 * money — shown in full (money redaction is the clinician view only).
 */
import type { BillSummary } from '../api/types';
import { formatPence, formatUtcDate } from '../lib/format';
import { paymentKindLabel, paymentPhaseCopy } from '../lib/patientStatusCopy';
import { Card, Help } from '../ui/kit';
import { IconPayments } from '../ui/icons';
import { PatientStatusBadge } from '../ui/PatientStatusBadge';
import { CardIconHeader } from '../ui/sections';

/** Group bills by `orderCaseId`, preserving first-seen order; a bill with no
 * order id (backend gap) falls into one trailing ungrouped bucket. */
function groupByOrder(bills: BillSummary[]): Array<{ orderCaseId?: string; bills: BillSummary[] }> {
  const groups: Array<{ orderCaseId?: string; bills: BillSummary[] }> = [];
  const index = new Map<string | undefined, number>();
  for (const bill of bills) {
    const key = bill.orderCaseId;
    const at = index.get(key);
    if (at === undefined) {
      index.set(key, groups.length);
      groups.push({ orderCaseId: key, bills: [bill] });
    } else {
      groups[at].bills.push(bill);
    }
  }
  return groups;
}

function BillRow({ bill }: { bill: BillSummary }) {
  // The Stripe-hosted document, if issueCaptureInvoice has run for this payment
  // — null/undefined is a normal, permanent state (never captured, or no Stripe
  // customer vault for a guest), so the link simply doesn't render.
  const invoiceUrl = bill.invoicePdfUrl ?? bill.invoiceHostedUrl;
  return (
    // Amount + phase on the top line, the description below — reads cleanly even
    // in the narrow dashboard side rail (no awkward badge wrap).
    <li className="gps-data-row" style={{ display: 'grid', gap: '6px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 'var(--gps-s-2)' }}>
        {/* Own bill — the patient legitimately sees their own money. */}
        <span style={{ color: 'var(--gps-text)', fontWeight: 700, fontSize: '22px', lineHeight: 1.15, fontFamily: 'var(--gps-font-display)' }}>{formatPence(bill.amountPence)}</span>
        {/* Sentence-case phase (+ "Not charged yet" detail) — never a SCREAMING pill. */}
        <PatientStatusBadge status={paymentPhaseCopy(bill.phase)} showDetail />
      </div>
      <span style={{ fontSize: '13px', color: 'var(--gps-muted)' }}>
        {paymentKindLabel(bill.kind)} · {formatUtcDate(bill.occurredAt)}
      </span>
      {/* THE LINK NAMES ITS OWN BILL. A billing history is a LIST, so "Download
          invoice" repeats once per row — and a screen-reader user cycling the
          links hears the same four words N times with nothing to tell them
          apart (WCAG 2.4.4, link purpose). The date is what distinguishes one
          bill from another on this card, so it goes in the accessible name; the
          visible text stays short because sighted users have the row for
          context.

          The label also follows what the link actually IS. `invoicePdfUrl` is a
          file and "Download" is accurate; the hosted page is a page, and
          promising a download that opens a web view is a small lie the patient
          notices. */}
      {invoiceUrl ? (
        <a
          href={invoiceUrl}
          target="_blank"
          rel="noreferrer"
          style={{ fontSize: '13px', color: 'var(--gps-link)' }}
        >
          {bill.invoicePdfUrl ? 'Download invoice' : 'View invoice'}
          <span className="gps-sr-only">
            {' '}for {paymentKindLabel(bill.kind)} on {formatUtcDate(bill.occurredAt)} (opens in a new tab)
          </span>
        </a>
      ) : null}
    </li>
  );
}

export function BillsCard({ bills, orderNameById }: { bills: BillSummary[]; orderNameById: Map<string, string> }) {
  const groups = groupByOrder(bills);
  return (
    <Card className="gps-card-interactive">
      <CardIconHeader icon={<IconPayments />} title="Billing history" />
      {bills.length === 0 ? (
        <Help style={{ marginBottom: 0 }}>No payments yet — your card is only charged after a clinician approves.</Help>
      ) : (
        <div style={{ display: 'grid', gap: 'var(--gps-s-4)' }}>
          {groups.map((group) => {
            const name = group.orderCaseId ? orderNameById.get(group.orderCaseId) : undefined;
            return (
              <div key={group.orderCaseId ?? '__ungrouped'}>
                {name ? (
                  <div
                    style={{
                      fontSize: '11px',
                      fontWeight: 700,
                      letterSpacing: '0.05em',
                      textTransform: 'uppercase',
                      color: 'var(--gps-muted)',
                      marginBottom: '6px',
                    }}
                  >
                    {name}
                  </div>
                ) : null}
                <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
                  {group.bills.map((bill) => (
                    <BillRow key={bill.paymentId} bill={bill} />
                  ))}
                </ul>
              </div>
            );
          })}
        </div>
      )}
    </Card>
  );
}
